import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { PreConfiguredMeal, OrderType, CustomerProfile, Restaurant, OrderNotFoundException, OrderApiException, OrderErrorCode, OrderWithContext, TagType, MealType, Menu, BYOMealType } from '@/models';
import { Routes, isGroupOrderRoute } from '@/router';
import { AnalyticsEventCategory } from '@/services/google/google-analytics.services';
import some from 'lodash/some';
import { GroupOrderErrorPageType } from '@/views/GroupOrder/GroupOrderError/cmg-group-order-error';
import { AddToBagEvent } from '@/services/analytics/events';
import { AnalyticsMealType } from '@/services/analytics/platforms/adobe-analytics/models';
import { AdobeCartAddEvent } from '@/services/analytics/platforms/adobe-analytics/adobe-events';
import { getAddToBagInteraction } from '@/services/analytics/platforms/adobe-analytics/helpers';
import { AddToBagWebInteraction } from '@/services/analytics/platforms/adobe-analytics/constants';
import { isPceBYOProtein } from '@/helpers';
import { MaxEntreeErrorEvent } from '@/services/analytics/events/max-byoc-error-event';
@Component({
  name: 'CmgPreConfiguredMealCard',
})
export default class CmgPreConfiguredMealCard extends Vue {
  @Prop({ required: true, type: Object })
  mealData!: PreConfiguredMeal;

  @Prop({ required: true, type: Number })
  cardWidth!: number;

  @Prop({ required: true, type: Number })
  linkPosition!: number;

  isOrderStart = false;

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get imageUrl(): string | undefined {
    const image = this.mealData.primaryImages.find(img => img.imageCategory == 'WEB');
    return image && image.imageUrl;
  }

  get dietaryTags(): string {
    return this.mealData?.dietaryTags[0];
  }

  get secondaryImageUrl(): string | undefined {
    const image = this.mealData.secondaryImages.find(img => img.imageCategory == 'WEB');
    return image && image.imageUrl;
  }

  get mealEditText(): string {
    return this.mealData.mealType == MealType.BuildYourOwn ? 'CUSTOMIZE' : 'MAKE IT MY OWN';
  }

  get mealPrice(): string {
    return (this.$services.order.isPickingUp() ? this.mealData.mealPrice : this.mealData.mealDeliveryPrice).toFixed(2);
  }

  get mealContentDescription(): string {
    return this.mealData?.description ?? '';
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get showCard(): boolean {
    return this.isEnrolledInLoyalty || !this.isExclusive;
  }

  get showRewardsPill(): boolean {
    return this.isExclusive;
  }

  get isExclusive(): boolean {
    return some(this.mealData.mealTags, ['type', TagType.Rewards]);
  }

  get exclusiveText(): string {
    return this.mealData.mealTags?.find(tag => tag.type == TagType.Rewards)?.value || '';
  }

  get selectedRestaurant(): Restaurant | null {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get isEntreeAvailable(): boolean {
    return this.mealHasEntree && this.mealData.entree?.isItemAvailable;
  }

  get mealHasEntree(): boolean {
    return !!this.mealData.entree?.itemId;
  }

  // Initially preconfigured meals were meant to have one entree only,
  // but now we have an extra protein side on a preconfigured meal
  // card and maybe someday we'll have a drink on there. This logic 
  // assumes there will only be one side or drink if there is no entree.
  get isEntreeOrSideOrDrinkAvailable(): boolean {
    if (this.mealHasEntree) {
      return this.isEntreeAvailable;
    } else {
      return (!!this.mealData.sides.length && this.mealData.sides[0].isItemAvailable) ||
             (!!this.mealData.drinks.length && this.mealData.drinks[0].isItemAvailable);
    }    
  }

  get hasUnavailableItems(): boolean {
    return this.mealData.entree?.contents.filter(x => !x.isItemAvailable).length > 0 ||
      this.mealData.sides.filter(x => !x.isItemAvailable).length > 0 ||
      this.mealData.drinks.filter(x => !x.isItemAvailable).length > 0;
  }

  get formattedUnavailableItems(): string {
    return this.mealData.entree?.contents.filter(x => !x.isItemAvailable).map(a => a.itemName)
      .concat(this.mealData.sides.filter(x => !x.isItemAvailable).map(a => a.itemName)
        .concat(this.mealData.drinks.filter(x => !x.isItemAvailable).map(a => a.itemName))).join(', ');
  }

  get activeMenu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }
  
  getEnergyLabelByCountry(){
    if(this.countryCode === 'GB') {
      return 'kcal';
    }
    return 'cal'
  }

  getMoneyLabelByCountry(){
    if(this.countryCode === 'GB') {
      return '£';
    }
    return '$'
  }

  async findAChipotleIfNeeded(): Promise<void> {
    if (!this.selectedRestaurant && !this.$services.order.isCustomerParticipantOfGroupOrder()) {
      await new Promise<void>(resolve => {
        this.$services.ui.setModalVisibility('findAChipotle', true, {
          onClose: () => {
            resolve();
          },
        });
        this.$services.ui.setModalVisibility('toast', true, {
          body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
          bodyOnly: true,
        });
      });
    }
  }

  async onClickAddToBag(event: any) {
    await this.findAChipotleIfNeeded();

    if (this.selectedRestaurant) {
      if (this.hasUnavailableItems) {
        this.$services.ui.showUnavailableIngredientsModal(this.mealData, this.mealData.mealName, this.navigateToMealBuilderEdit);
        this.$services.ui.firstFocus();
      } else {
        let preFilledMealName = '';
        if (this.$services.order.isCustomerParticipantOfGroupOrder()) {
          preFilledMealName = this.$appStore.state.order.groupOrderParticpantName;
        } else if (this.$appStore.state.customer.profile && !this.$appStore.state.customer.profile.isGuest) {
          preFilledMealName = this.$appStore.state.customer.profile.firstName;
        }
        this.$services.ui.pushEventTargetAsLastSelectedElement(event);
        this.$emit('add-to-bag');
        this.$services.ui.setModalVisibility('mealBuilderMealName', true, {
          beforeClose: mealName => this.addMealToOrder(mealName),
          mealName: preFilledMealName,
        });
        this.$services.ui.firstFocus();
      }
    }
  }

  async onClickMakeItMyOwn(event: any) {
    await this.findAChipotleIfNeeded();
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$emit('make-it-my-own');

    if (this.hasUnavailableItems && this.mealData.mealType !== MealType.BuildYourOwn) {
      // Do not show customers an uneccessary 'Unavailable item' modal for BYO
      this.$services.ui.showUnavailableIngredientsModal(this.mealData, this.mealData.mealName, this.navigateToMealBuilderEdit);
      this.$services.ui.firstFocus();
    } else {
      // Do not show customers an unecessary 'Changing It Up' modal for BYO
      if (this.mealData.mealType == MealType.BuildYourOwn) {
        this.navigateToMealBuilderEdit();
      } else {
        this.$services.ui.showMakeItMyOwnModal(this.mealData.mealName, this.navigateToMealBuilderEdit);
        this.$services.ui.firstFocus();
      }
    }
  }

  async addMealToOrder(mealName: string) {
    let order = this.$services.order.getCurrentOrder();
    const isGroupOrder = order && order.order.orderType == OrderType.Group;

    try {
      if (!this.$appStore.state.customer.jwt && !isGroupOrder) {
        await this.$services.customer.createAutogeneratedUser();
      }

      if (!this.$appStore.state.restaurant.selectedRestaurant) {
        const error = 'no restaurant selected even though one must be selected for Whats New to be visible!';
        this.$services.logger.unreachable(error);
        throw new Error(error);
      }

      if (!order) {
        order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant.restaurantNumber, OrderType.Regular);
        this.isOrderStart = true;
      } else {
        this.isOrderStart = false;
      }

      if (!order) {
        const error = 'currentOrder was somehow null after creating a new order!';
        this.$services.logger.unreachable(error);
        throw new Error(error);
      }

      try {
        await this.$services.order.addPreconfiguredMealToOrder(order, this.mealData.mealId, mealName);

        if (this.$services.order.isCustomerParticipantOfGroupOrder()) {
          this.$router.push({ name: Routes.GroupOrderConfirmation });
        } else {
          this.$services.bag.setBagVisibility(true);
          Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
          this.$router.push({ name: Routes.Home });
        }
        this.$services.googleAnalytics.sendCustomEvent({ category: AnalyticsEventCategory.Featured, action: 'addtobag', label: this.mealData.mealName });
        this.sendAnalyticsEvents(order, this.mealData);
      } catch (e: any) {
        this.$services.logger.error('Could not add preconfigured meal to order ' + JSON.stringify(e));

        if (e instanceof OrderNotFoundException && isGroupOrder) {
          // The most likely scenario for not found is because a host cancelled the Group Order
          this.$services.order.setPendingGroupOrder(null, '');
          this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.NotFound } });
          return;
        } else if (e instanceof OrderApiException && e.errors.length > 0) {
          const error = e.errors[0];

          if (error) {
            if (isGroupOrder && error.errorCode == OrderErrorCode.OrderCompleted) {
              this.$services.bag.setBagVisibility(false);
              this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.NotFound } });
              return;
            }

            if (isGroupOrder && error.errorCode == OrderErrorCode.MaxGroupParticipantsExceeded) {
              this.$services.bag.setBagVisibility(false);
              this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.MaxParticipantCountReached } });
              return;
            }

            if (error.errorCode == OrderErrorCode.MaxEntreeLimitExceeded) {
              this.$services.analytics.send(new MaxEntreeErrorEvent(
                e.errors[0], 
                this.mealData.preconfiguredMealName ?? this.mealData.mealName,
                this.$router.currentRoute.name ?? '',
                isPceBYOProtein(this.mealData, this.activeMenu) ? BYOMealType.BYOProtein : BYOMealType.BYOChips));
              this.$services.ui.setModalVisibility('maxEntreeLimitModal', true, { errorMessage: error.errorData, errorCode: error.errorCode });
              return;
            }

            if (error.errorCode == OrderErrorCode.MaxBYOLimitExceeded) {
              this.$services.ui.showMaxBYOFMLimitModal(order, error, this.$router.currentRoute.name ?? '', this.mealData.mealName, () => this.replaceMealInOrder(mealName, order));
              return;
            }

            this.$services.ui.setModalVisibility('mealBuilderMealName', false);
            throw e;
          }

          this.$services.logger.error('Something went really wrong when adding item to bag: ', undefined, e);
          this.$services.ui.setModalVisibility('globalErrorToast', true);
        }
      }
    } catch {
      this.$services.ui.setModalVisibility('globalErrorToast', true);
    }
  }

  private sendAnalyticsEvents(order: OrderWithContext, meal: PreConfiguredMeal): void {
    this.$services.analytics.addLinkClickedEventToStore(getAddToBagInteraction(AddToBagWebInteraction, this.mealData.mealName, this.linkPosition));
    this.$services.analytics.setAddToBagEventToStore(new AdobeCartAddEvent(new AddToBagEvent(meal, AnalyticsMealType.Standard, this.isOrderStart, undefined, true, meal.mealType), this.$appStore, this.$services));
  }

  private navigateToMealBuilderEdit(): void {
    const route = `/order/${isGroupOrderRoute(this.$route) ? 'group/' : ''}build/edit/pcm/${this.mealData.mealId}`;
    this.$router.push(route);
    this.$services.bag.setBagVisibility(false);
  }

  private async replaceMealInOrder(mealName: string, order: OrderWithContext | null): Promise<void> {
    const mealToBeReplaced = order?.order.meals?.find(meal => meal.entrees?.find(entree => this.activeMenu?.entrees?.find(e => e.itemType == 'BYOProtein' && entree.menuItemId == e.itemId)));
    try {
      if (order) {
        await this.$services.order.removeMealFromOrder(order, mealToBeReplaced?.mealId ?? '');
        await this.addMealToOrder(mealName);
      }
    } catch(e: any) {
      this.$services.ui.setModalVisibility('globalErrorToast', true);
      this.$services.logger.error('replaceMealInOrder: ', e!);
    }  
    finally {
      this.$services.ui.setModalVisibility('basicError', false);
    }
  }
}
